import React from "react";
import { CHART_COLORS, LINE_CHART_COLOR, MOCK_CHART_DATA, MOCK_PIE_DATA } from "../constants";
import { cn } from "@/lib/utils";
import type { ReportSection } from "../types";

export const SectionChart: React.FC<{
  section: ReportSection;
  heightClass?: string;
}> = ({ section, heightClass = "h-[250px]" }) => {
  const data = section.chartData && section.chartData.length > 0
    ? section.chartData
    : section.chartType === "pie" ? MOCK_PIE_DATA : MOCK_CHART_DATA;
  const width = 720;
  const height = 250;
  const margin = { top: 18, right: 24, bottom: 46, left: 52 };
  const plotWidth = width - margin.left - margin.right;
  const plotHeight = height - margin.top - margin.bottom;
  const values = data.map((item) => Number(item.value || 0)).filter(Number.isFinite);
  const maxValue = Math.max(...values, 1);
  const yForValue = (value: number) => margin.top + (1 - value / maxValue) * plotHeight;
  const yTicks = Array.from({ length: 4 }, (_, index) => (maxValue * index) / 3);
  const xForIndex = (index: number) =>
    margin.left + (data.length <= 1 ? plotWidth / 2 : (index / (data.length - 1)) * plotWidth);
  const linePath = data
    .map((item, index) => `${index === 0 ? "M" : "L"} ${xForIndex(index).toFixed(2)} ${yForValue(Number(item.value || 0)).toFixed(2)}`)
    .join(" ");

  return (
    <div className={cn("w-full overflow-x-auto", heightClass)}>
      <svg viewBox={`0 0 ${width} ${height}`} width="100%" height="100%" role="img" aria-label={section.chartMetricLabel ?? "Report chart"}>
        <rect width={width} height={height} fill="#ffffff" />
        {section.chartType === "pie" ? (
          <PieSvg data={data} metricLabel={section.chartMetricLabel} />
        ) : (
          <>
            {yTicks.map((tick) => {
              const y = yForValue(tick);
              return (
                <g key={tick}>
                  <line x1={margin.left} x2={width - margin.right} y1={y} y2={y} stroke="#e2e8f0" strokeDasharray="3 3" />
                  <text x={margin.left - 8} y={y + 4} textAnchor="end" fontSize="10" fill="#475569">
                    {Math.round(tick)}
                  </text>
                </g>
              );
            })}
            {section.chartType === "line" ? (
              <>
                <path d={linePath} fill="none" stroke={LINE_CHART_COLOR} strokeWidth="2.5" />
                {data.map((item, index) => (
                  <circle key={`${item.name}-${index}`} cx={xForIndex(index)} cy={yForValue(Number(item.value || 0))} r="4" fill={LINE_CHART_COLOR}>
                    <title>{`${item.name}: ${item.value}`}</title>
                  </circle>
                ))}
              </>
            ) : (
              data.map((item, index) => {
                const slot = plotWidth / Math.max(data.length, 1);
                const barWidth = Math.max(14, Math.min(48, slot * 0.58));
                const x = margin.left + index * slot + (slot - barWidth) / 2;
                const y = yForValue(Number(item.value || 0));
                return (
                  <rect key={`${item.name}-${index}`} x={x} y={y} width={barWidth} height={Math.max(1, margin.top + plotHeight - y)} rx="4" fill={CHART_COLORS[index % CHART_COLORS.length]}>
                    <title>{`${item.name}: ${item.value}`}</title>
                  </rect>
                );
              })
            )}
            {data.map((item, index) =>
              index % Math.max(1, Math.ceil(data.length / 8)) === 0 ? (
                <text key={`${item.name}-label`} x={xForIndex(index)} y={height - 20} textAnchor="middle" fontSize="10" fill="#475569">
                  {String(item.name).length > 14 ? `${String(item.name).slice(0, 12)}...` : item.name}
                </text>
              ) : null,
            )}
            <text x={width / 2} y={height - 4} textAnchor="middle" fontSize="10" fill="#64748b">
              {section.chartMetricLabel ?? "Value"}
            </text>
          </>
        )}
      </svg>
    </div>
  );
};

const PieSvg: React.FC<{ data: Array<{ name: string; value: number }>; metricLabel?: string }> = ({ data, metricLabel }) => {
  const cx = 360;
  const cy = 112;
  const radius = 76;
  const total = Math.max(data.reduce((sum, item) => sum + Number(item.value || 0), 0), 1);
  let startAngle = -Math.PI / 2;

  return (
    <>
      {data.map((item, index) => {
        const value = Number(item.value || 0);
        const angle = (value / total) * Math.PI * 2;
        const endAngle = startAngle + angle;
        const x1 = cx + Math.cos(startAngle) * radius;
        const y1 = cy + Math.sin(startAngle) * radius;
        const x2 = cx + Math.cos(endAngle) * radius;
        const y2 = cy + Math.sin(endAngle) * radius;
        const largeArc = angle > Math.PI ? 1 : 0;
        const path = `M ${cx} ${cy} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} Z`;
        startAngle = endAngle;
        return (
          <path key={`${item.name}-${index}`} d={path} fill={CHART_COLORS[index % CHART_COLORS.length]} stroke="#ffffff" strokeWidth="2">
            <title>{`${item.name}: ${item.value}`}</title>
          </path>
        );
      })}
      <text x={cx} y={cy} textAnchor="middle" fontSize="11" fontWeight="600" fill="#334155">
        {metricLabel ?? "Value"}
      </text>
    </>
  );
};
